import { useTranslation } from "react-i18next";
import { StyleSheet, TouchableOpacity, View } from "react-native";

import { ThemedText } from "@/components/ThemedText";
import { useThemeColor } from "@/hooks/useThemeColor";

export type HomeTabValue = "feed" | "map";

type Props = {
  value: HomeTabValue;
  onChange: (next: HomeTabValue) => void;
};

export const HOME_TAB_SWITCHER_HEIGHT = 44;

const UNDERLINE_HEIGHT = 2;

export function HomeTabSwitcher({ value, onChange }: Props) {
  const { t } = useTranslation();
  const color = useThemeColor({}, "text");
  const accentColor = useThemeColor({}, "tabBarBackground");
  const backgroundColor = useThemeColor({}, "background");

  const tabs: { value: HomeTabValue; labelKey: string }[] = [
    { value: "feed", labelKey: "HOME_TAB_FEED" },
    { value: "map", labelKey: "HOME_TAB_MAP" },
  ];

  return (
    <View style={[styles.container, { backgroundColor }]}>
      <View style={styles.inner}>
        {tabs.map((tab) => {
          const isActive = tab.value === value;
          return (
            <TouchableOpacity
              key={tab.value}
              onPress={() => onChange(tab.value)}
              style={styles.tab}
            >
              <ThemedText
                style={[
                  styles.label,
                  { color: isActive ? accentColor : color },
                  isActive && styles.labelActive,
                ]}
              >
                {t(tab.labelKey)}
              </ThemedText>
              <View
                style={[
                  styles.underline,
                  {
                    backgroundColor: isActive ? accentColor : "transparent",
                  },
                ]}
              />
            </TouchableOpacity>
          );
        })}
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    height: HOME_TAB_SWITCHER_HEIGHT,
    width: "100%",
    alignItems: "center",
    justifyContent: "flex-end",
    paddingHorizontal: 16,
  },
  inner: {
    flexDirection: "row",
    width: "100%",
    maxWidth: 360,
  },
  tab: {
    flex: 1,
    alignItems: "center",
    justifyContent: "flex-end",
    paddingVertical: 0,
  },
  label: {
    fontSize: 14,
    fontWeight: "500",
    paddingBottom: 8,
  },
  labelActive: {
    fontWeight: "600",
  },
  underline: {
    height: UNDERLINE_HEIGHT,
    width: "100%",
    borderTopLeftRadius: UNDERLINE_HEIGHT,
    borderTopRightRadius: UNDERLINE_HEIGHT,
  },
});

export default HomeTabSwitcher;
